/**
 * "HER DANCER SIGN-IN ENDED" HAS TO SURVIVE THE SIGN-OUT RELOAD, for a
 * promoted Jr Staff dancer (DECISIONS 48 final review, round 1, P3).
 *
 * A child-only dancer whose cookie the server refuses lands straight on the
 * sign-in card, and dancer-session-context.tsx sets the notice in the same
 * breath. A PROMOTED Jr Staff dancer holds a Firebase principal as well, so
 * a refused cookie sends her through the full sign-out first
 * (runDancerLogout, then lib/auth.ts signOut), and that sign-out ends in
 * window.location.reload(). Every piece of evidence the ordinary path reads
 * is gone on the far side of that reload: React state is new, signOut
 * forgot the remembered dancer home, and the server cleared the cookie with
 * its own "session_ended" answer, so the next status check reads exactly
 * like a first visit. She used to land on the plain sign-in card with no
 * word of why, while the How-To guide said the card would tell her.
 *
 * So the provider writes this one-tab note BEFORE starting that sign-out,
 * reads it once when the next page mounts, and forgets it as soon as a
 * status answer arrives.
 *
 * sessionStorage, not localStorage, on purpose (the same choice
 * reloadGuard.ts makes for the same reason): it survives the reload the
 * sign-out itself performs and dies with the tab, so a note left behind by
 * a crash can never greet somebody else days later on a shared phone. It
 * holds no name and no id, only "a dancer sign-in ended in this tab", which
 * is less than the remembered dancer home the ordinary path already reads.
 *
 * Every access is wrapped: private windows and locked-down webviews can
 * throw on the storage object itself. Unavailable storage reads as "no
 * note", which only ever loses the notice, never shows it wrongly.
 */

const SIGN_IN_ENDED_KEY = 'ldg-hub-dancer-sign-in-ended';

function tabStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Called just before a promoted Jr Staff dancer's full sign-out starts. */
export function rememberDancerSignInEnded(): void {
  const store = tabStorage();
  if (!store) return;
  try {
    store.setItem(SIGN_IN_ENDED_KEY, '1');
  } catch {
    // Quota or a locked store: the notice is lost, nothing else is.
  }
}

/** Read once, at mount. Never removes the note, so a double render cannot eat it. */
export function hasDancerSignInEndedNote(): boolean {
  const store = tabStorage();
  if (!store) return false;
  try {
    return store.getItem(SIGN_IN_ENDED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Called as soon as a real status answer arrives: the note has done its job. */
export function forgetDancerSignInEnded(): void {
  const store = tabStorage();
  if (!store) return;
  try {
    store.removeItem(SIGN_IN_ENDED_KEY);
  } catch {
    // Nothing to do; the note dies with the tab anyway.
  }
}
